import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import {
  addDepartmentTicketComment,
  addDepartmentTicketCommentForHelper,
  changeDepartmentTicketStatus,
  changeDepartmentTicketStatusForHelper,
  getDepartmentTicket,
  getDepartmentTicketForHelper,
  uploadDepartmentTicketFile,
} from "../../api/departmentTicketService";
import { handleDownloadFile } from "../../api/handleDownloadFile";
import {
  DepartmentTicketDetailsDto,
  DepartmentTicketFileDto,
  DepartmentTicketStatus,
} from "../../interfaces/Tickets/departmentTicket";
import { formatDateTimeInChicago } from "../../helpers/formatingFunctions";
import { getStoredEmployeeId } from "../../utils/employeeIdStorage";
import { priorityLabel, statusLabel } from "./DepartmentTicketsPage";

const displayUser = (fullName?: string | null, email?: string | null) =>
  fullName || email || "Unknown user";

const statusOptions = [
  { value: DepartmentTicketStatus.Open, label: "Open" },
  { value: DepartmentTicketStatus.InProgress, label: "In Progress" },
  {
    value: DepartmentTicketStatus.WaitingForSender,
    label: "Waiting for Sender",
  },
  {
    value: DepartmentTicketStatus.WaitingForReceiver,
    label: "Waiting for Receiver",
  },
  { value: DepartmentTicketStatus.Resolved, label: "Resolved" },
  { value: DepartmentTicketStatus.Closed, label: "Closed" },
  { value: DepartmentTicketStatus.Cancelled, label: "Cancelled" },
];

const normalizeStatusKey = (value: string) =>
  value.replace(/[^a-z0-9]/gi, "").toLowerCase();

const statusLookup = statusOptions.reduce<
  Record<string, DepartmentTicketStatus>
>((lookup, option) => {
  lookup[normalizeStatusKey(option.label)] = option.value;
  lookup[normalizeStatusKey(DepartmentTicketStatus[option.value])] =
    option.value;
  return lookup;
}, {});

const normalizeDepartmentTicketStatus = (
  value: DepartmentTicketStatus | string | number | undefined,
): DepartmentTicketStatus => {
  if (typeof value === "number") {
    return value as DepartmentTicketStatus;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    const numericValue = Number(trimmed);

    if (
      !Number.isNaN(numericValue) &&
      DepartmentTicketStatus[numericValue] !== undefined
    ) {
      return numericValue as DepartmentTicketStatus;
    }

    const enumValue =
      DepartmentTicketStatus[trimmed as keyof typeof DepartmentTicketStatus];

    if (typeof enumValue === "number") {
      return enumValue;
    }

    const normalizedValue = statusLookup[normalizeStatusKey(trimmed)];

    if (normalizedValue !== undefined) {
      return normalizedValue;
    }
  }

  return DepartmentTicketStatus.Open;
};

const DepartmentTicketDetailsPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const { token } = useAuth();
  const [ticket, setTicket] = React.useState<DepartmentTicketDetailsDto | null>(
    null,
  );
  const [loading, setLoading] = React.useState(true);
  const [message, setMessage] = React.useState("");
  const [status, setStatus] = React.useState<DepartmentTicketStatus>(
    DepartmentTicketStatus.Open,
  );
  const [note, setNote] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [downloadingFileId, setDownloadingFileId] = React.useState<
    string | null
  >(null);

  const currentTicketStatus = ticket
    ? normalizeDepartmentTicketStatus(ticket.status)
    : DepartmentTicketStatus.Open;

  const isClosed = currentTicketStatus === DepartmentTicketStatus.Closed;
  const isHelperTicketView =
    new URLSearchParams(location.search).get("helper") === "true";

  const loadTicket = React.useCallback(async () => {
    if (!id || !token) return;
    setLoading(true);
    setError(null);
    try {
      const employeeId = getStoredEmployeeId();
      const data = isHelperTicketView
        ? await getDepartmentTicketForHelper(id, token)
        : await getDepartmentTicket(id, token, employeeId);
      setTicket(data);
      setStatus(normalizeDepartmentTicketStatus(data.status));
    } catch (err: any) {
      setError(err?.message ?? "Ticket not found or you do not have access.");
    } finally {
      setLoading(false);
    }
  }, [id, isHelperTicketView, token]);

  React.useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  const addComment = async () => {
    if (!id || !token || !message.trim()) return;
    setBusy(true);
    try {
      const employeeId = getStoredEmployeeId();

      const payload = { message, authorEmployeeId: employeeId };

      await (isHelperTicketView
        ? addDepartmentTicketCommentForHelper(id, payload, token)
        : addDepartmentTicketComment(id, payload, token));
      setMessage("");
      await loadTicket();
    } catch (err: any) {
      setError(err?.response?.data ?? err?.message ?? "Unable to add comment.");
    } finally {
      setBusy(false);
    }
  };

  const updateStatus = async () => {
    if (!id || !token) return;
    setBusy(true);
    try {
      const employeeId = getStoredEmployeeId();
      const data = isHelperTicketView
        ? await changeDepartmentTicketStatusForHelper(
            id,
            status,
            note,
            token,
            employeeId,
          )
        : await changeDepartmentTicketStatus(
            id,
            status,
            note,
            token,
            employeeId,
          );
      setTicket(data);
      setStatus(normalizeDepartmentTicketStatus(data.status));
      setNote("");
    } catch (err: any) {
      setError(
        err?.response?.data ??
          err?.message ??
          "You are not authorized to change this ticket status.",
      );
    } finally {
      setBusy(false);
    }
  };

  const uploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!id || !token || !file) return;
    setBusy(true);
    try {
      const employeeId = getStoredEmployeeId();
      await uploadDepartmentTicketFile(id, file, token, employeeId);
      await loadTicket();
    } catch (err: any) {
      setError(err?.response?.data ?? err?.message ?? "Unable to upload file.");
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  };

  const downloadFile = async (file: DepartmentTicketFileDto) => {
    if (!token) return;

    setDownloadingFileId(file.id);
    try {
      await handleDownloadFile(file, "departmentTicket", token);
    } finally {
      setDownloadingFileId(null);
    }
  };

  if (loading) return <CircularProgress />;
  if (!ticket) {
    return <Alert severity="error">{error ?? "Ticket unavailable."}</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Box
        flexWrap={{ xs: "wrap", lg: "nowrap" }}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Typography variant="h4">{ticket.title}</Typography>
          <Typography color="text.secondary">
            Created {formatDateTimeInChicago(ticket.createdDate)} · Updated{" "}
            {formatDateTimeInChicago(ticket.lastUpdatedDate)}
          </Typography>
        </Box>
        <Chip
          label={statusLabel(currentTicketStatus)}
          color={
            currentTicketStatus === DepartmentTicketStatus.Closed
              ? "default"
              : "primary"
          }
        />
      </Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {isClosed && (
        <Alert severity="info">
          Closed tickets are read-only until reopened.
        </Alert>
      )}
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Description</Typography>
                  <Typography sx={{ whiteSpace: "pre-wrap", mt: 1 }}>
                    {ticket.description}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography variant="h6">Chat / Comments</Typography>
                  <List>
                    {ticket.comments.map((comment) => (
                      <ListItem
                        key={comment.id}
                        alignItems="flex-start"
                        divider
                      >
                        <ListItemText
                          primary={`${displayUser(
                            comment.authorFullName,
                            comment.authorEmail,
                          )} · ${comment.authorDepartment || "No department"}`}
                          secondary={
                            <>
                              <Typography component="span" variant="caption">
                                {formatDateTimeInChicago(comment.createdDate)}
                              </Typography>
                              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                                {comment.message}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Add a message"
                    value={message}
                    disabled={isClosed || busy}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                  <Button
                    sx={{ mt: 2 }}
                    variant="contained"
                    disabled={isClosed || busy || !message.trim()}
                    onClick={addComment}
                  >
                    Send Message
                  </Button>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Ticket Details</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography>Sender: {ticket.senderDepartment}</Typography>
                  <Typography>Receiver: {ticket.receiverDepartment}</Typography>
                  <Typography>
                    Priority: {priorityLabel(ticket.priority)}
                  </Typography>
                  <Typography>
                    Assigned user: {ticket.assignedUserFullName || "Unassigned"}
                  </Typography>
                  <Typography>
                    Due date:{" "}
                    {ticket.dueDate
                      ? new Date(ticket.dueDate).toLocaleDateString()
                      : "None"}
                  </Typography>
                  <Typography>
                    Created by: {displayUser(ticket.createdByFullName)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography variant="h6">Status</Typography>
                  <TextField
                    select
                    fullWidth
                    sx={{ my: 2 }}
                    label="Status"
                    value={status}
                    onChange={(e) => setStatus(Number(e.target.value))}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    label="Status note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <Button
                    fullWidth
                    sx={{ mt: 2 }}
                    variant="contained"
                    disabled={busy || status === currentTicketStatus}
                    onClick={updateStatus}
                  >
                    Update Status
                  </Button>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography variant="h6">Files</Typography>
                  <List dense>
                    {ticket.files.map((file) => (
                      <ListItem key={file.id} divider>
                        <ListItemText
                          primary={file.fileName}
                          secondary={`${Math.round(file.size / 1024)} KB · ${displayUser(
                            file.uploadedByFullName,
                          )} · ${formatDateTimeInChicago(file.uploadedDate)}`}
                        />
                        <Button
                          size="small"
                          onClick={() => downloadFile(file)}
                          disabled={downloadingFileId === file.id}
                        >
                          {downloadingFileId === file.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            "Download"
                          )}
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                  <Button
                    component="label"
                    variant="outlined"
                    disabled={isClosed || busy}
                  >
                    Upload File
                    <input type="file" hidden onChange={uploadFile} />
                  </Button>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography variant="h6">Status History</Typography>
                  <List dense>
                    {ticket.statusHistory.map((history) => (
                      <ListItem key={history.id} divider>
                        <ListItemText
                          primary={`${
                            history.fromStatus !== undefined
                              ? statusLabel(history.fromStatus)
                              : "Created"
                          } → ${statusLabel(history.toStatus)}`}
                          secondary={`${formatDateTimeInChicago(
                            history.changedDate,
                          )} · ${displayUser(
                            history.changedByFullName,
                          )}${history.note ? ` · ${history.note}` : ""}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};

export default DepartmentTicketDetailsPage;
